// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Translation glossary UI (one pair at a time)
// 2. Separation of Concerns - Language list from upload config; edits via parent API
// 3. Modular Architecture - Reuses SettingsList for array options
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * Translation Glossary: dropdown matches voice upload languages (→ English).
 * Pair keys: `{code}_en` — list from API `voice/upload-languages`, fallback to public JSON.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
} from '@mui/material';
import SettingsList from './SettingsList';
import { fetchVoiceUploadLanguages } from '../../utils/voiceLanguageConfig';

const EXCLUDED_FROM_GLOSSARY = new Set(['en', 'other']);

const TranslationGlossarySettings = ({
  settings,
  canUpdateSettings,
  onAddOption,
  onEditOption,
  onDeleteOption,
  onUpdateSetting,
}) => {
  const [langConfig, setLangConfig] = useState([]);
  const [configError, setConfigError] = useState(null);
  const [selectedKey, setSelectedKey] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await fetchVoiceUploadLanguages();
        const langs = (all || []).filter(
          (l) => l.code && !EXCLUDED_FROM_GLOSSARY.has(l.code),
        );
        if (!cancelled) {
          setLangConfig(langs);
          setConfigError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setConfigError(
            'Could not load voice languages (API or /config/voice_language.json)',
          );
          setLangConfig([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pairOptions = useMemo(
    () =>
      langConfig.map((l) => ({
        key: `${l.code}_en`,
        label: `${l.name} → English`,
      })),
    [langConfig],
  );

  useEffect(() => {
    if (!selectedKey && pairOptions.length > 0) {
      setSelectedKey(pairOptions[0].key);
    }
  }, [pairOptions, selectedKey]);

  const activeSetting = useMemo(
    () => settings.find((s) => s.setting_key === selectedKey),
    [settings, selectedKey],
  );

  return (
    <Box>
      {configError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {configError}
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose the source language (same list as voice upload). Glossary maps
        source terms to English for translation and analysis helpers.
      </Typography>
      <FormControl fullWidth sx={{ maxWidth: 480, mb: 3 }}>
        <InputLabel id="glossary-pair-label">Language pair</InputLabel>
        <Select
          labelId="glossary-pair-label"
          label="Language pair"
          value={selectedKey}
          onChange={(e) => setSelectedKey(e.target.value)}
          disabled={pairOptions.length === 0}
          sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#13213c' } }}
        >
          {pairOptions.map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {!activeSetting && selectedKey && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No glossary row for <strong>{selectedKey}</strong> in the database. Run{' '}
          <code>alembic upgrade head</code> (revision <code>z5_tr_glossary_pairs</code>)
          or production seed to create missing pairs.
        </Alert>
      )}
      {activeSetting && (
        <SettingsList
          settings={[activeSetting]}
          onAddOption={onAddOption}
          onEditOption={onEditOption}
          onDeleteOption={onDeleteOption}
          onUpdateSetting={onUpdateSetting}
          canUpdateSettings={canUpdateSettings}
        />
      )}
    </Box>
  );
};

export default TranslationGlossarySettings;
